import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import { getModulePermissions } from '../../utils/permissions';

const defaultFormState = {
  name: '',
  isMandatory: false,
  isActive: true,
};

const DocumentSettings = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [formData, setFormData] = useState(defaultFormState);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const permissions = useMemo(() => getModulePermissions('settings-document'), []);
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;
  const canManage = canCreate || canEdit;
  const hasActions = canManage || canDelete;
  const isViewOnly = permissions.view && !hasActions;

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiRequest('/api/admin/settings/documents', {}, navigate);
      setDocuments(response.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load documents.');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const resetForm = () => {
    setFormData(defaultFormState);
    setEditingId(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'isMandatory' || name === 'isActive') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = formData.name.trim();

    const isEditing = Boolean(editingId);

    if (isEditing && !canEdit) {
      setError('You do not have permission to edit documents.');
      return;
    }

    if (!isEditing && !canCreate) {
      setError('You do not have permission to create documents.');
      return;
    }

    if (!trimmedName) {
      setError('Document name is required.');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccessMessage('');

    const payload = {
      name: trimmedName,
      isMandatory: formData.isMandatory,
      isActive: formData.isActive,
    };

    try {
      const endpoint = isEditing
        ? `/api/admin/settings/documents/${editingId}`
        : '/api/admin/settings/documents';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await apiRequest(endpoint, { method, body: payload }, navigate);
      setSuccessMessage(response.message || (isEditing ? 'Document updated successfully.' : 'Document created successfully.'));
      resetForm();
      loadDocuments();
    } catch (err) {
      setError(err.message || 'Unable to save document.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (document) => {
    if (!canEdit) {
      navigate('/access-denied', { state: { from: '/settings/documents' } });
      return;
    }
    setEditingId(document.id);
    setFormData({
      name: document.name || '',
      isMandatory: Boolean(document.isMandatory),
      isActive: Boolean(document.isActive),
    });
    setSuccessMessage('');
    setError('');
  };

  const handleDelete = async (document) => {
    if (!canDelete) {
      navigate('/access-denied', { state: { from: '/settings/documents' } });
      return;
    }

    if (document.isSystem) {
      return;
    }

    const result = await Swal.fire({
      title: 'Delete document?',
      text: 'Are you sure you want to delete this document? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionId(document.id);
    setError('');
    setSuccessMessage('');

    try {
      const response = await apiRequest(
        `/api/admin/settings/documents/${document.id}`,
        { method: 'DELETE' },
        navigate
      );
      setSuccessMessage(response.message || 'Document deleted successfully.');
      if (editingId === document.id) {
        resetForm();
      }
      loadDocuments();
    } catch (err) {
      setError(err.message || 'Unable to delete document.');
    } finally {
      setActionId(null);
    }
  };

  const isSaveDisabled = useMemo(() => formData.name.trim().length === 0 || isSaving, [formData.name, isSaving]);
  const isEditing = Boolean(editingId);

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Documents Management</h4>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/settings')}>
            Back
          </button>
        </div>

        {isViewOnly && (
          <div className="alert alert-info" role="alert">
            You have view-only access. Create and edit actions are disabled.
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}

        <div className="row g-4">
          {canManage ? (
            <div className="col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">{isEditing ? 'Edit Document Name' : 'Create Document Name'}</h5>
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none"
                        onClick={resetForm}
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Document Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter document name"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Is Mandatory</label>
                      <select
                        className="form-select"
                        name="isMandatory"
                        value={String(formData.isMandatory)}
                        onChange={handleChange}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Is Active</label>
                      <select
                        className="form-select"
                        name="isActive"
                        value={String(formData.isActive)}
                        onChange={handleChange}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="d-grid">
                      <button type="submit" className="btn btn-danger" disabled={isSaveDisabled}>
                        {isSaving ? 'Saving…' : isEditing ? 'Update Document' : 'Create Document'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            <div className="col-lg-4">
              <div className="alert alert-info mb-0" role="alert">
                You can view documents but cannot create or edit them.
              </div>
            </div>
          )}

          <div className="col-lg-8">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">All Document Names</h5>
                  {isLoading && (
                    <div className="spinner-border text-primary" role="status" aria-hidden="true" />
                  )}
                </div>

                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th>Document Name</th>
                        <th>Mandatory</th>
                        <th>Active</th>
                        {hasActions && (
                          <th className="text-center" style={{ width: '20%' }}>
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {documents.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            {isLoading ? 'Loading documents…' : 'No documents found. Add one using the form.'}
                          </td>
                        </tr>
                      ) : (
                        documents.map((document, index) => (
                          <tr key={document.id}>
                            <td>{index + 1}</td>
                            <td className="fw-semibold">{document.name}</td>
                            <td>
                              <span
                                className={`badge rounded-pill ${document.isMandatory ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'}`}
                              >
                                {document.isMandatory ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge rounded-pill ${document.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}
                              >
                                {document.isActive ? 'Yes' : 'No'}
                              </span>
                            </td>
                            {hasActions && (
                              <td>
                                <div className="d-flex gap-2 justify-content-center">
                                  {canEdit && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => handleEdit(document)}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleDelete(document)}
                                      disabled={document.isSystem || actionId === document.id}
                                      title={document.isSystem ? 'System documents cannot be deleted' : undefined}
                                    >
                                      {actionId === document.id ? 'Deleting…' : 'Delete'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DocumentSettings;
